import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import ColumnGroupingTable from "../../table/ColumnGroupingTable.component";
import { useHedgeRatioContext } from "../../../context/HedgeRatioContext";
import {
  Card,
  Typography,
  Box,
  TableCell,
  TableRow,
  TableBody,
  Table,
  Button,
  InputLabel,
  TextField,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import InlineDropdown from "../../common/InlineDropdown/InlineDropdown";
import {
  flattenHRAccountData,
  getMappedBusinessEntityIDs,
  getPercentage,
  prepareDataForHedgeRatio,
} from "../../../utils/utils";
import {
  DENOMINATOR_AGGREGATION,
  DIMENSION,
  HR,
  HR_DENOMINATOR,
  HR_NUMERATOR,
  MEASURE,
  NUMERATOR_AGGREGATION,
} from "../../../utils/constants";
import useStyles from "./HRModelerAnalytics.styles";
import { useUserContext } from "../../../context/UserContext";
import { useTreasuryKRISummaryContext } from "../../../context/TreasuryKRISummaryContext";
import HRModelerAnalyticsEmail from "./HRModelerAnalyticsEmail";
import ProgressSteps from "../../common/Steps/ProgressSteps";
import { useApprovalContext } from "../../../context/ApprovalContext";
import HRModelerAnalyticsExport from "./HRModelerAnalyticsExport";

const mapFigureDivisor = (figureDivisor) => ({
  id: figureDivisor.value,
  name: figureDivisor.name,
});

const mapDecimalsIn = (decimalsIn) => ({
  id: decimalsIn.value,
  name: decimalsIn.name,
});

const mapModelDatabase = (modelDatabase) => ({
  id: modelDatabase.modelName,
  name: modelDatabase.modelName,
});

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.primary,
}));

const HRModelerAnalyticsFiltersAndReports = ({
  toSave = false,
  toCompare = false,
} = {}) => {
  const { classes } = useStyles(toCompare);
  const [isWritableSfc, setIsWritableSfc] = useState(false);
  const { selectedUserDetailLoading, selectedUserDetail, isAdmin } =
    useUserContext();

  const {
    hrFiltersData,
    hrFiltersDataLoading,
    hrGapResultData,
    setHrGapResultData,
    hrGapResultDataLoading,
    hrGapResultDataError,
    fetchHrGapResultReport,
    numeratorAccountNum,
    denominatorAccountNum,
    selectedBusinessEntityID,
    setSelectedBusinessEntityID,
    setLocalCurrency,
    localCurrency,
    selectedModelDatabase,
    setSelectedModelDatabase,
    modelDatabases,
    setModelDatabases,
    figureDivisors,
    selectedFigureDivisor,
    setSelectedFigureDivisor,
    decimalsIn,
    selectedDecimalsIn,
    setSelectedDecimalsIn,
    sumOfNumerators,
    setSumOfNumerators,
    sumOfDenominators,
    setSumOfDenominators,
    hedgeRatioPercentage,
    setHedgeRatioPercentage,
    selectedMonthEnd,
    setSelectedMonthEnd,
  } = useHedgeRatioContext();

  const {
    fetchApprovalFlowData,
    approvalFlow,
    setApprovalFlow,
    saveApprovalData,
    sendApproverEmailNotifyForSelectedReport,
    sendEmailNotifyLoading,
  } = useApprovalContext();

  const { metricsHistoryUpsertAndSave, metricsHistoryUpsertAndSaveLoading } =
    useTreasuryKRISummaryContext();

  const { regions, email } = selectedUserDetail || {};

  const allsfcs = useMemo(() => {
    return (
      regions
        ?.flatMap((region) => region?.sfcs?.map((sfc) => sfc) || [])
        .sort((a, b) => a.businessEntityID.localeCompare(b.businessEntityID)) ||
      []
    );
  }, [regions]);

  const mappedBusinessEntityIDs = getMappedBusinessEntityIDs(
    hrFiltersData,
    regions,
  );

  const mappedFigureDivisors = [
    ...(figureDivisors || []).map(mapFigureDivisor),
  ];

  const mappedDecimalsIn = [...(decimalsIn || []).map(mapDecimalsIn)];

  useEffect(() => {
    const entityObject = hrFiltersData?.find(
      (item) => item.entity === selectedBusinessEntityID,
    );
    setModelDatabases(entityObject?.dateModels || []);
    const { localCurrency: currency, writable } =
      allsfcs?.find(
        (sfc) => sfc.businessEntityID === selectedBusinessEntityID,
      ) || {};
    setLocalCurrency(currency);
    setIsWritableSfc(writable);
  }, [
    selectedBusinessEntityID,
    hrFiltersData,
    setModelDatabases,
    allsfcs,
    setLocalCurrency,
  ]);

  useEffect(() => {
    if (selectedModelDatabase) {
      const modelObject = modelDatabases?.find((model) => {
        return model?.modelName === selectedModelDatabase;
      });
      const monthEndFromModelDatabase = modelObject?.monthEnd
        ? dayjs(modelObject.monthEnd?.replace("Z", ""))
        : null;
      setSelectedMonthEnd(monthEndFromModelDatabase);
    }
  }, [modelDatabases, selectedModelDatabase, setSelectedMonthEnd]);

  const mappedModelDatabases = [
    ...(modelDatabases || []).map(mapModelDatabase),
  ];

  const APPROVAL_FLOW_PARAMS = {
    region: null,
    businessEntityID: selectedBusinessEntityID,
    selectedReportDate: selectedMonthEnd,
    isGlobal: false,
    isModelerReport: true,
    isTreasuryKRIReport: false,
    isKRISummaryReport: false,
    isSFCKRIGlobalSummaryReport: false,
    isRegionKRIGlobalSummaryReport: false,
    isTFSCKRIGlobalSummaryReport: false,
    measure: MEASURE.SQUARE_HEDGE_RATIO_KRI,
    dimension: DIMENSION.INTEREST_RATE_RISK,
    dimension2: null,
  };

  const handleSubmitBtn = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      fetchHrGapResultReport(HR);
      fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
    } else {
      setHrGapResultData([]);
      setApprovalFlow([]);
    }
  };

  const handleSendApproverEmailNotify = () => {
    if (selectedModelDatabase && selectedBusinessEntityID) {
      sendApproverEmailNotifyForSelectedReport({
        ...APPROVAL_FLOW_PARAMS,
        isGlobal: null,
        isModelerReport: null,
        isTreasuryKRIReport: null,
        isKRISummaryReport: null,
        isSFCKRIGlobalSummaryReport: null,
        isRegionKRIGlobalSummaryReport: null,
        isTFSCKRIGlobalSummaryReport: null,
      });
    }
  };

  const handleStepApproval = ({
    currentStep,
    currentApprover,
    type,
    comment,
    reportDate,
  } = {}) => {
    return saveApprovalData({
      currentApprover,
      type,
      comment,
      reportDate,
    })
      .then(() => {
        return fetchApprovalFlowData({ ...APPROVAL_FLOW_PARAMS });
      })
      .then(() => {
        return sendApproverEmailNotifyForSelectedReport({
          ...APPROVAL_FLOW_PARAMS,
          isGlobal: null,
          isModelerReport: null,
          isTreasuryKRIReport: null,
          isKRISummaryReport: null,
          isSFCKRIGlobalSummaryReport: null,
          isRegionKRIGlobalSummaryReport: null,
          isTFSCKRIGlobalSummaryReport: null,
        });
      });
  };

  const scaleAndFormatData = useCallback((accounts, divisor, decimals) => {
    if (!accounts || accounts.length === 0) {
      return [];
    }

    return accounts.map((account) => {
      const newDatas = {};
      for (const [key, value] of Object.entries(account.datas || {})) {
        const scaledValue = Number(value) / divisor;

        const formattedValue = scaledValue.toFixed(decimals);
        newDatas[key] = formattedValue;
      }

      let newSubAccounts = [];
      if (account.subAccounts && account.subAccounts.length > 0) {
        newSubAccounts = scaleAndFormatData(
          account.subAccounts,
          divisor,
          decimals,
        );
      }

      return {
        ...account,
        datas: newDatas,
        subAccounts: newSubAccounts,
      };
    });
  }, []);

  const decimals =
    Number(selectedDecimalsIn) >= 0 ? Number(selectedDecimalsIn) : 0;

  const finalHrGapResultData = useMemo(() => {
    if (!hrGapResultData || hrGapResultData.length === 0) {
      return [];
    }

    const divisor = Number(selectedFigureDivisor) || 1;

    return scaleAndFormatData(hrGapResultData, divisor, decimals);
  }, [hrGapResultData, selectedFigureDivisor, decimals, scaleAndFormatData]);

  const dynamicColumns =
    finalHrGapResultData &&
    finalHrGapResultData?.length > 0 &&
    finalHrGapResultData?.[0]?.datas
      ? Object.keys(finalHrGapResultData?.[0]?.datas)?.map((key) => {
          return {
            id: key,
            label: key,
            minWidth: 80,
            align: "right",
          };
        })
      : [];

  const columns = [
    { id: "aggregation", label: "Agg.", minWidth: 60, align: "left" },
    {
      id: "reportingAccountName",
      label: "Reporting Account Name",
      minWidth: 160,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const exportColumns = [
    { id: "level", label: "Account Level", minWidth: 60, align: "left" },
    { id: "aggregation", label: "Agg.", minWidth: 60, align: "left" },
    {
      id: "formattedReportingAccountName",
      label: "Formatted Reporting Account Name",
      minWidth: 160,
      align: "left",
    },
    ...dynamicColumns,
  ];

  const handleSaveHedgeRatio = () => {
    if (
      parseInt(selectedFigureDivisor) === 1 &&
      parseInt(selectedDecimalsIn) === 0
    ) {
      metricsHistoryUpsertAndSave([
        {
          dimension: DIMENSION.INTEREST_RATE_RISK,
          dimension2: null,
          measure: MEASURE.HR_NUMERATOR,
          businessEntityID: selectedBusinessEntityID,
          value: `${hrNum}`,
          modelDatabase: selectedModelDatabase,
          isGlobal: false,
        },
        {
          dimension: DIMENSION.INTEREST_RATE_RISK,
          dimension2: null,
          measure: MEASURE.HR_DENOMINATOR,
          businessEntityID: selectedBusinessEntityID,
          value: `${hrDenom}`,
          modelDatabase: selectedModelDatabase,
          isGlobal: false,
        },
        {
          dimension: DIMENSION.INTEREST_RATE_RISK,
          dimension2: null,
          measure: MEASURE.SQUARE_HEDGE_RATIO_KRI,
          businessEntityID: selectedBusinessEntityID,
          value: `${hedgeRatioPercentage}%`,
          modelDatabase: selectedModelDatabase,
          isGlobal: false,
        },
      ]);
    } else {
      alert(
        "Please change the Figure Divisor selection to 'Raw' and Decimals selection to 'No Decimal' before saving.",
      );
    }
  };

  const numeratorAccount = finalHrGapResultData?.find(
    ({ accountNum }) => accountNum === numeratorAccountNum,
  );
  const denominatorAccount = finalHrGapResultData?.find(
    ({ accountNum }) => accountNum === denominatorAccountNum,
  );

  const hrNumerator = prepareDataForHedgeRatio(
    numeratorAccount ? [numeratorAccount] : [],
    numeratorAccountNum,
    HR_NUMERATOR,
    false,
  );

  const hrDenominator = prepareDataForHedgeRatio(
    denominatorAccount ? [denominatorAccount] : [],
    denominatorAccountNum,
    HR_DENOMINATOR,
    false,
  );

  const hrNumeratorSubAccounts = prepareDataForHedgeRatio(
    numeratorAccount?.subAccounts ?? [],
    numeratorAccountNum,
    HR_NUMERATOR,
    true,
  );
  const hrDenominatorSubAccounts = prepareDataForHedgeRatio(
    denominatorAccount?.subAccounts ?? [],
    denominatorAccountNum,
    HR_DENOMINATOR,
    true,
  );

  const preprocessedData = [
    ...hrNumeratorSubAccounts,
    ...hrNumerator,
    ...hrDenominatorSubAccounts,
    ...hrDenominator,
  ];

  const exportPreprocessedData = [
    ...flattenHRAccountData(hrNumerator, NUMERATOR_AGGREGATION),
    ...flattenHRAccountData(hrDenominator, DENOMINATOR_AGGREGATION),
  ];

  const sumAccountDatas = useCallback((data, decimals) => {
    if (!data || !data.datas) return 0;

    const datas = data.datas;
    const values = Object.values(datas);

    const rawTotalSum = values.reduce((accumulator, currentValue) => {
      return accumulator + Number(currentValue);
    }, 0);

    const roundedTotalSum = parseFloat(rawTotalSum.toFixed(decimals));
    return roundedTotalSum;
  }, []);

  useEffect(() => {
    if (finalHrGapResultData && finalHrGapResultData.length) {
      const totalHRNumerator = sumAccountDatas(numeratorAccount, decimals);
      const totalHRDenominator = sumAccountDatas(denominatorAccount, decimals);
      setSumOfNumerators(totalHRNumerator);
      setSumOfDenominators(totalHRDenominator);
      setHedgeRatioPercentage(
        getPercentage(totalHRNumerator, totalHRDenominator, decimals),
      );
    }
  }, [
    setHedgeRatioPercentage,
    setSumOfNumerators,
    setSumOfDenominators,
    sumOfDenominators,
    sumOfNumerators,
    numeratorAccount,
    denominatorAccount,
    finalHrGapResultData,
    decimals,
    sumAccountDatas,
  ]);

  const hrNum = sumOfNumerators?.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const hrDenom = sumOfDenominators?.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return (
    <Card
      className={classes.HRModelerAnalyticsCardResponsive}
      style={{
        padding: "6px",
        marginBottom: "20px",
      }}
    >
      <Typography
        sx={{
          color: "#6a6a6a",
          marginBottom: 2,
          fontWeight: "bold",
        }}
      >
        The below Balance Sheet shows the Hedge Ratio Analytics Report (Modeler
        View) with Overall Hedge Ratio percentage based on the selected filters.
      </Typography>
      <>
        {!hrGapResultDataError &&
        finalHrGapResultData &&
        finalHrGapResultData?.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pr: "10px",
              }}
            >
              {approvalFlow?.length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleSendApproverEmailNotify}
                  sx={{
                    fontSize: "0.8rem",
                    boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                    height: "38px",
                    mt: "-10px",
                  }}
                  disabled={
                    selectedUserDetailLoading ||
                    hrFiltersDataLoading ||
                    hrGapResultDataLoading ||
                    !selectedBusinessEntityID ||
                    !selectedModelDatabase ||
                    sendEmailNotifyLoading
                  }
                >
                  Send Reminder Notification
                </Button>
              )}
            </Box>
            <ProgressSteps
              stepsData={approvalFlow}
              isGlobal={false}
              isModelerReport={true}
              isTreasuryKRIReport={false}
              isKRISummaryReport={false}
              isSFCKRIGlobalSummaryReport={false}
              isRegionKRIGlobalSummaryReport={false}
              isTFSCKRIGlobalSummaryReport={false}
              reportDate={selectedMonthEnd}
              loggedInEmail={email}
              handleStepApproval={handleStepApproval}
            />
          </>
        ) : (
          <Typography
            sx={{
              color: "#6a6a6a",
              marginBottom: 2,
              fontStyle: "italic",
            }}
          >
            Please generate the report to view the Approval Progress
          </Typography>
        )}
      </>

      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"business-entity"}>Business Entity</InputLabel>
          <Autocomplete
            options={mappedBusinessEntityIDs}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedBusinessEntityIDs.find(
                (item) => item.id === selectedBusinessEntityID,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedBusinessEntityID(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Business Entity"
                size="small"
              />
            )}
            sx={{ width: 180 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputLabel id={"model-database"}>Model Database</InputLabel>
          <Autocomplete
            options={mappedModelDatabases}
            getOptionLabel={(option) => option?.name || ""}
            value={
              mappedModelDatabases.find(
                (item) => item.id === selectedModelDatabase,
              ) || null
            }
            onChange={(_, newValue) => {
              setSelectedModelDatabase(newValue ? newValue.id : null);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={selectedUserDetailLoading || hrFiltersDataLoading}
            ListboxProps={{
              style: {
                width: "max-content",
                minWidth: "100%",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Select Model Database"
                size="small"
              />
            )}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleSubmitBtn}
            sx={{
              fontSize: "0.8rem",
              boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
              height: "38px",
              mt: 3,
            }}
            disabled={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading ||
              !selectedBusinessEntityID ||
              !selectedModelDatabase
            }
          >
            Submit
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Figures In"
            labelId="show-figures-in"
            options={mappedFigureDivisors}
            selectedValue={selectedFigureDivisor}
            handleChange={setSelectedFigureDivisor}
            placeholder={"Show Figures In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading
            }
          />
        </Grid>
        {localCurrency && (
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel id="currency-type">Currency</InputLabel>
            <Typography
              sx={{
                color: "#6a6a6a",
                fontWeight: "bold",
                marginTop: 1,
              }}
            >
              {localCurrency}
            </Typography>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InlineDropdown
            label="Show Decimal In"
            labelId="show-decimal-in"
            options={mappedDecimalsIn}
            selectedValue={selectedDecimalsIn}
            handleChange={setSelectedDecimalsIn}
            placeholder={"Show Decimals In"}
            dropdownWidth={150}
            isLoading={
              selectedUserDetailLoading ||
              hrFiltersDataLoading ||
              hrGapResultDataLoading
            }
          />
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          }}
        >
          <div className={classes.HRModelerAnalyticsExportBtn}>
            <HRModelerAnalyticsExport
              columns={exportColumns}
              datas={exportPreprocessedData}
              disabled={
                hrGapResultDataLoading ||
                !exportPreprocessedData?.length ||
                hrGapResultDataError
              }
            />
          </div>
        </Grid>
        {!toCompare && (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <HRModelerAnalyticsEmail
              columns={exportColumns}
              datas={exportPreprocessedData}
              selectedMonthEnd={selectedMonthEnd}
              hedgeRatioPercentage={hedgeRatioPercentage}
              disabled={
                hrGapResultDataLoading ||
                !exportPreprocessedData?.length ||
                hrGapResultDataError
              }
            />
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {!hrGapResultDataLoading && hrGapResultDataError ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                There was an error in fetching the data. Please try again later.
              </Typography>
            </Item>
          ) : (!hrGapResultDataLoading && !finalHrGapResultData) ||
            (!hrGapResultDataLoading && !finalHrGapResultData?.length) ? (
            <Item>
              <Typography
                variant="p"
                component="p"
                sx={{
                  color: "#adadad",
                  minHeight: "250px",
                }}
              >
                No data available for the selected filters.
              </Typography>
            </Item>
          ) : (
            <ColumnGroupingTable
              columns={columns}
              datas={preprocessedData}
              loading={hrGapResultDataLoading}
              tableHeight={400}
              freezeParentColumnCount={2}
              disablePagination={true}
              applyThousandsSeparator={true}
            />
          )}
        </Grid>
        {!hrGapResultDataLoading &&
          finalHrGapResultData?.length > 0 &&
          !hrGapResultDataError && (
            <Grid size={{ xs: 12, md: 12, lg: 12 }}>
              <Box sx={{ marginBottom: 2 }}>
                <Table
                  sx={{
                    width: "30%",
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    borderRadius: "3px",
                    overflow: "hidden",
                    boxShadow: "1px 1px 2px",
                    marginLeft: "auto",
                    marginRight: "auto",
                    backgroundColor: "#EBFFD6",
                  }}
                >
                  <TableBody>
                    <TableRow
                      sx={{
                        "& td": {
                          fontWeight: 500,
                        },
                      }}
                    >
                      <TableCell sx={{ border: "1px solid #ccc" }}>
                        <b>Sum of HR Numerators</b>
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ccc" }}>
                        {hrNum}
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        "& td": {
                          fontWeight: 500,
                        },
                      }}
                    >
                      <TableCell sx={{ border: "1px solid #ccc" }}>
                        <b>Sum of HR Denominators</b>
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ccc" }}>
                        {hrDenom}
                      </TableCell>
                    </TableRow>
                    <TableRow
                      sx={{
                        "& td": {
                          fontWeight: 500,
                        },
                      }}
                    >
                      <TableCell sx={{ border: "1px solid #ccc" }}>
                        <b>Hedge Ratio (%)</b>
                      </TableCell>
                      <TableCell sx={{ border: "1px solid #ccc" }}>
                        {hedgeRatioPercentage}%
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {toSave && !toCompare && (isWritableSfc || isAdmin) && (
                  <div className={classes.SaveBtn}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveHedgeRatio}
                      sx={{
                        fontSize: "0.8rem",
                        boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.1)",
                        height: "40px",
                      }}
                      disabled={metricsHistoryUpsertAndSaveLoading}
                    >
                      Save Data
                    </Button>
                  </div>
                )}
              </Box>
            </Grid>
          )}
      </Grid>
    </Card>
  );
};

export default HRModelerAnalyticsFiltersAndReports;
